import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { Asset, AssetSearchParams, AssetCreateRequest, AssetUpdateRequest, AssetImage, SetPrimaryImageRequest, UploadAssetImageResponse } from '@/types/asset.types';
import { getAssetStatusLabel } from '@/config/tenant';
import { PaginatedResponse } from '@/types/api.types';
import i18n from '@/i18n';

// Query keys
export const assetKeys = {
  all: ['assets'] as const,
  lists: () => [...assetKeys.all, 'list'] as const,
  list: (params: AssetSearchParams) => [...assetKeys.lists(), params] as const,
  details: () => [...assetKeys.all, 'detail'] as const,
  detail: (id: number | string) => [...assetKeys.details(), id] as const,
  timeline: (id: number | string) => [...assetKeys.detail(id), 'timeline'] as const,
  incidents: (id: number | string) => [...assetKeys.detail(id), 'incidents'] as const,
  images: (id: number | string) => [...assetKeys.detail(id), 'images'] as const,
};

// API response wrapper type
interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

// API Asset response type (from backend)
interface ApiAsset {
  assetId: string;
  assetCode: string;
  name: string | null;
  assetDescription: string;
  epcTag: string | null;
  locationId: number;
  locationName: string | null;
  assetClassId: number | null;
  assetClassName: string | null;
  assetStatus: number;
  status: string;
  createdDate: string;
  notes: string | null;
  entity: string;
  isActive: boolean;
  ownerClientId?: number | null;
  ownerClientName?: string | null;
}

// Transform API asset to frontend format
function transformAsset(apiAsset: ApiAsset): Asset {
  return {
    assetId: apiAsset.assetId as unknown as number, // AssetId is a string code in API
    name: apiAsset.name || apiAsset.assetDescription || apiAsset.assetCode || '',
    description: apiAsset.assetDescription,
    locationId: apiAsset.locationId,
    locationName: apiAsset.locationName || '',
    assetClassId: apiAsset.assetClassId || undefined,
    assetClassName: apiAsset.assetClassName || undefined,
    entity: apiAsset.entity,
    assetStatus: apiAsset.assetStatus,
    assetStatusName: getAssetStatusLabel(apiAsset.assetStatus),
    isActive: apiAsset.isActive ?? true,
    epc: apiAsset.epcTag || undefined,
    comments: apiAsset.notes || undefined,
    createdDate: apiAsset.createdDate,
    ownerClientId: apiAsset.ownerClientId ?? undefined,
    ownerClientName: apiAsset.ownerClientName ?? undefined,
  };
}

// Search assets
export function useAssets(params: AssetSearchParams) {
  return useQuery({
    queryKey: assetKeys.list(params),
    queryFn: async () => {
      const response = await api.post<ApiResponse<{ data: ApiAsset[]; totalRecords: number }>>('/api/asset/search', {
        searchStr: params.searchStr,
        locationId: params.locationId,
        status: params.status,
        page: params.page || 1,
        pageSize: params.pageSize || 25,
      });
      // Transform API response to our expected format
      const apiData = response.data.data;
      return {
        items: apiData.data.map(transformAsset),
        totalCount: apiData.totalRecords,
        totalPages: Math.ceil(apiData.totalRecords / (params.pageSize || 25)),
        page: params.page || 1,
        pageSize: params.pageSize || 25,
      } as PaginatedResponse<Asset>;
    },
  });
}

// Export assets to Excel (respects the current search + status/location filters)
export function useExportAssets() {
  return useMutation({
    mutationFn: async (params: { searchStr?: string; status?: number; locationId?: number }) => {
      const response = await api.post('/api/asset/export', {
        searchStr: params.searchStr,
        status: params.status,
        locationId: params.locationId,
      }, {
        responseType: 'blob',
      });
      return response.data as Blob;
    },
  });
}

// Lookup assets for dropdowns (lightweight, server-side search)
export interface AssetLookupItem {
  id: string;
  text: string;
  description: string | null;
  location: string | null;
}

export function useAssetLookup(searchStr: string, noEpc = false) {
  return useQuery({
    queryKey: ['assets', 'lookup', searchStr, noEpc] as const,
    queryFn: async () => {
      const response = await api.get<ApiResponse<AssetLookupItem[]>>('/api/asset/lookup', {
        params: { searchStr, noEpc },
      });
      return response.data.data;
    },
    enabled: searchStr.length >= 2,
  });
}

// Get single asset
export function useAsset(id: number | string, enabled = true) {
  return useQuery({
    queryKey: assetKeys.detail(id),
    queryFn: async () => {
      const response = await api.get<ApiResponse<ApiAsset>>(`/api/asset/get`, {
        params: { assetId: id }
      });
      return transformAsset(response.data.data);
    },
    enabled: enabled && (typeof id === 'string' ? id.length > 0 : id > 0),
  });
}

// API Timeline entry type
interface ApiTimelineEntry {
  eventType: string;
  status: string;
  description: string;
  date: string;
  userId: number;
  /**
   * Who performed it. Optional so a UI deployed ahead of the API keeps
   * rendering the id instead of a blank author.
   */
  userName?: string | null;
}

// Transformed timeline entry for UI
export interface AssetTimelineEntry {
  id: string;
  date: string;
  event: string;
  description: string;
  user: string;
}

function transformTimelineEntry(entry: ApiTimelineEntry, index: number): AssetTimelineEntry {
  // Resolve the status label through i18n (per-tenant + per-language).
  const statusLabel = getAssetStatusLabel(entry.status);
  return {
    id: `timeline-${index}`,
    date: entry.date,
    event: entry.description || i18n.t('common:timeline.statusChangedTo', { status: statusLabel }),
    description: entry.description || '',
    // The name when the server could resolve one, the bare id only as a
    // fallback — a deleted account, or a row written by a background process.
    // "Usuario #17" is not an identity anyone can act on.
    user: entry.userName?.trim()
      ? entry.userName.trim()
      : entry.userId
        ? i18n.t('common:timeline.userNumber', { id: entry.userId })
        : i18n.t('common:timeline.system'),
  };
}

// Get asset timeline
export function useAssetTimeline(id: number | string, enabled = true) {
  return useQuery({
    queryKey: assetKeys.timeline(id),
    queryFn: async () => {
      const response = await api.get<ApiResponse<ApiTimelineEntry[]>>(`/api/asset/timeline`, {
        params: { assetId: id }
      });
      return response.data.data.map(transformTimelineEntry);
    },
    enabled: enabled && (typeof id === 'string' ? id.length > 0 : (id as number) > 0),
  });
}

// API Incident entry type
interface ApiIncidentEntry {
  assetIncidentId: number;
  assetId: string;
  notes: string | null;
  incidentType: number;
  incidentDate: string;
  resolved: boolean;
  resolvedDate: string | null;
}

// Transformed incident for UI
export interface AssetIncidentEntry {
  id: number;
  assetId: string;
  type: string;
  typeCode: number;
  notes: string;
  date: string;
  resolved: boolean;
  resolvedDate: string | null;
}

function transformIncident(incident: ApiIncidentEntry): AssetIncidentEntry {
  return {
    id: incident.assetIncidentId,
    assetId: incident.assetId,
    type: i18n.t(`common:incidents.types.${incident.incidentType}`, {
      defaultValue: i18n.t('common:labels.unknown'),
    }),
    typeCode: incident.incidentType,
    notes: incident.notes || '',
    date: incident.incidentDate,
    resolved: incident.resolved,
    resolvedDate: incident.resolvedDate,
  };
}

// Get asset incidents
export function useAssetIncidents(id: number | string, enabled = true) {
  return useQuery({
    queryKey: assetKeys.incidents(id),
    queryFn: async () => {
      const response = await api.get<ApiResponse<ApiIncidentEntry[]>>(`/api/asset/incidents`, {
        params: { assetId: id }
      });
      return response.data.data.map(transformIncident);
    },
    enabled: enabled && (typeof id === 'string' ? id.length > 0 : (id as number) > 0),
  });
}

// Create asset
export function useCreateAsset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: AssetCreateRequest) => {
      // Forward assetId when the form provides one — the backend honours it
      // for tenant-meaningful identifiers (e.g. "OXY-001") and only falls
      // back to a generated hex id when the field is left blank.
      // isNewAsset disambiguates create from update on /api/asset/save when
      // the id is present (without it the controller would treat the call
      // as an update lookup and 404 on a brand-new id).
      const response = await api.post<ApiResponse<string>>('/api/asset/save', {
        assetId: data.assetId,
        isNewAsset: true,
        name: data.name,
        description: data.description,
        locationId: data.locationId,
        assetClassId: data.assetClassId,
        status: data.status,
        notes: data.notes,
      });
      return response.data.data; // Returns the actual saved assetId
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assetKeys.lists() });
    },
  });
}

// Update asset
export function useUpdateAsset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number | string; data: AssetUpdateRequest }) => {
      const response = await api.post<ApiResponse<string>>('/api/asset/save', {
        assetId: id,
        name: data.name,
        description: data.description,
        locationId: data.locationId,
        assetClassId: data.assetClassId,
        status: data.status,
        notes: data.notes,
      });
      return response.data.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: assetKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: assetKeys.lists() });
    },
  });
}

// Delete asset - Note: API doesn't have a delete endpoint, using soft delete via status change
export function useDeleteAsset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (_id: number | string) => {
      // The API doesn't have a direct delete - we could implement as status change if needed
      // For now, just invalidate the cache
      console.warn('Delete not implemented in API');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assetKeys.lists() });
    },
  });
}

// ─── P04 — v2 bulk import (CSV with code-based resolution + dry-run) ───
// The legacy `useBulkUploadAssets` below remains for TOCA scripts that
// already speak the old `ASSET_NUMBER,TAG_NUMBER,LOCATION` column shape.
export interface AssetBulkImportRowError {
  line: number;
  assetId: string;
  code: string;
  message: string;
}

export interface AssetBulkImportResponse {
  ok: boolean;
  entity: string;
  dryRun: boolean;
  totals: {
    created: number;
    skippedDuplicate: number;
    errors: number;
  };
  errors: AssetBulkImportRowError[];
  errorsTruncated: boolean;
}

export function useBulkImportAssetsV2() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ file, dryRun }: { file: File; dryRun: boolean }) => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post<AssetBulkImportResponse>(
        `/api/v2/assets/bulk-import?dryRun=${dryRun ? 'true' : 'false'}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return response.data;
    },
    onSuccess: (data) => {
      // Real imports invalidate the list. Dry-runs leave caches alone.
      if (!data.dryRun) {
        queryClient.invalidateQueries({ queryKey: assetKeys.lists() });
      }
    },
  });
}

// Bulk upload assets
export function useBulkUploadAssets() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post<ApiResponse<{ importedCount: number; duplicateCount: number }>>('/api/asset/upload/bulk', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assetKeys.lists() });
    },
  });
}

// Change asset status
export function useChangeAssetStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, status, reason }: { id: number; status: number | string; reason?: string }) => {
      const response = await api.put(`/api/assets/${id}/status`, { status, reason });
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: assetKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: assetKeys.lists() });
    },
  });
}

// Get assets for dropdown (lightweight list)
export function useAssetsDropdown() {
  return useQuery({
    queryKey: [...assetKeys.all, 'dropdown'],
    queryFn: async () => {
      const response = await api.post<ApiResponse<{ data: ApiAsset[]; totalRecords: number }>>('/api/asset/search', {
        page: 1,
        pageSize: 500, // Get more for dropdown
      });
      return response.data.data.data.map((asset) => ({
        assetId: asset.assetId,
        name: asset.assetDescription || asset.assetCode || asset.assetId,
      }));
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
}

// Search assets for async select (minimum 3 characters)
export function useAssetSearch(searchStr: string, enabled = true) {
  return useQuery({
    queryKey: [...assetKeys.all, 'search', searchStr],
    queryFn: async () => {
      const response = await api.post<ApiResponse<{ data: ApiAsset[]; totalRecords: number }>>('/api/asset/search', {
        searchStr,
        page: 1,
        pageSize: 20, // Limit results for dropdown
      });
      return response.data.data.data.map((asset) => ({
        assetId: asset.assetId,
        name: asset.assetDescription || asset.assetCode || asset.assetId,
        assetCode: asset.assetCode,
      }));
    },
    enabled: enabled && searchStr.length >= 3, // Only search with 3+ chars
    staleTime: 1000 * 60 * 2, // Cache for 2 minutes
    gcTime: 1000 * 60 * 5, // Keep in cache for 5 minutes
  });
}

// Get single asset for display (when editing work order with pre-selected asset)
export function useAssetById(assetId: string | null, enabled = true) {
  return useQuery({
    queryKey: [...assetKeys.all, 'byId', assetId],
    queryFn: async () => {
      const response = await api.get<ApiResponse<ApiAsset>>('/api/asset/get', {
        params: { assetId }
      });
      const asset = response.data.data;
      return {
        assetId: asset.assetId,
        name: asset.assetDescription || asset.assetCode || asset.assetId,
        assetCode: asset.assetCode,
      };
    },
    enabled: enabled && !!assetId,
    staleTime: 1000 * 60 * 5,
  });
}

// =====================
// Asset Images Hooks
// =====================

// Get asset images
export function useAssetImages(assetId: string, enabled = true) {
  return useQuery({
    queryKey: assetKeys.images(assetId),
    queryFn: async () => {
      const response = await api.get<ApiResponse<AssetImage[]>>('/api/asset/images', {
        params: { assetId }
      });
      return response.data.data;
    },
    enabled: enabled && !!assetId,
  });
}

// Upload asset image
export function useUploadAssetImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ assetId, file }: { assetId: string; file: File }) => {
      const formData = new FormData();
      formData.append('assetId', assetId);
      formData.append('file', file);

      const response = await api.post<ApiResponse<UploadAssetImageResponse>>('/api/asset/images/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: assetKeys.images(variables.assetId) });
      queryClient.invalidateQueries({ queryKey: assetKeys.detail(variables.assetId) });
      queryClient.invalidateQueries({ queryKey: assetKeys.lists() });
    },
  });
}

// Set primary image
export function useSetPrimaryImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: SetPrimaryImageRequest) => {
      const response = await api.post<ApiResponse<null>>('/api/asset/images/set-primary', request);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: assetKeys.images(variables.assetId) });
      queryClient.invalidateQueries({ queryKey: assetKeys.detail(variables.assetId) });
      queryClient.invalidateQueries({ queryKey: assetKeys.lists() });
    },
  });
}

// Delete asset image
export function useDeleteAssetImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ assetId: _assetId, imageId }: { assetId: string; imageId: number }) => {
      const response = await api.delete<ApiResponse<null>>(`/api/asset/images/${imageId}`);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: assetKeys.images(variables.assetId) });
      queryClient.invalidateQueries({ queryKey: assetKeys.detail(variables.assetId) });
      queryClient.invalidateQueries({ queryKey: assetKeys.lists() });
    },
  });
}
